import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type PropsWithChildren,
} from "react";

interface RouterValue {
  path: string;
  navigate(destination: string | number, replace?: boolean): void;
}

const RouterContext = createContext<RouterValue | null>(null);

function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export function RouterProvider({ children }: PropsWithChildren) {
  const [path, setPath] = useState(currentPath);

  useEffect(() => {
    const update = () => setPath(currentPath());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  const navigate = useCallback((destination: string | number, replace = false) => {
    if (typeof destination === "number") {
      window.history.go(destination);
      return;
    }
    if (replace) window.history.replaceState(null, "", destination);
    else window.history.pushState(null, "", destination);
    setPath(currentPath());
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const value = useMemo(() => ({ path, navigate }), [navigate, path]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error("useRouter must be used inside RouterProvider");
  return value;
}

export function Navigate({ to, replace = true }: { to: string; replace?: boolean }) {
  const { navigate } = useRouter();
  useEffect(() => navigate(to, replace), [navigate, replace, to]);
  return null;
}

export function NavLink({
  to,
  end = false,
  className,
  onClick,
  ...props
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "href"> & {
  to: string;
  end?: boolean;
  className?: string | ((active: boolean) => string | undefined);
}) {
  const { path, navigate } = useRouter();
  const pathname = path.split("?")[0];
  const active = end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
  const resolvedClassName = typeof className === "function" ? className(active) : className;

  return (
    <a
      {...props}
      href={to}
      className={resolvedClassName}
      onClick={(event) => {
        onClick?.(event);
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        navigate(to);
      }}
    />
  );
}
